import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';

import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  POS_PROFILE_SEARCH_CONFIG,
  POS_PROFILE_COLUMNS,
  POS_PROFILE_DEFAULT_COLUMNS_ORDER,
  POS_PROFILE_FLEXIBLE_COLUMNS_ORDER,
  POS_PROFILE_ACTION_COLUMN,
  getPosProfileSearchConfig,
} from './pos-profile.config';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  PosProfileService,
  PosProfile,
  PosProfileSearchParams,
} from './services/pos-profile.service';

// TypeScript interfaces are now imported from the service

@Component({
  selector: 'app-pos-profile',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    DialogModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './pos-profile.component.html',
  styleUrls: ['./pos-profile.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosProfileComponent implements OnInit {
  // Search configuration
  searchConfig = POS_PROFILE_SEARCH_CONFIG;

  // Table configuration (kept for reference, but using advanced table columns below)
  tableColumns = POS_PROFILE_COLUMNS;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'pos_profile' for POS permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.pos_profile.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.pos_profile.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.pos_profile.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.pos_profile.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.pos_profile.approve');
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () =>
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove() ||
      this.canRead(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_PROFILE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of POS_PROFILE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(POS_PROFILE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_PROFILE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of POS_PROFILE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Data signals with proper typing
  posProfile = signal<PosProfile[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    channelShopId: undefined,
    code: '',
    status: undefined,
    page: 1,
    size: 20,
  });

  // Selection with proper typing
  selectedPosProfile = signal<PosProfile[]>([]);

  // Computed properties
  hasSelected = computed(() => this.selectedPosProfile().length > 0);
  hasDeletable = computed(() => {
    const selected = this.selectedPosProfile();
    return selected.some((terminal) => !terminal.status);
  });
  hasActiveTerminals = computed(() =>
    this.selectedPosProfile().some((terminal) => terminal.status),
  );

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationTitle = signal('');
  confirmationButtons = signal<any[]>([]);
  confirmationAction = signal('');
  confirmationData = signal<any>(null);

  // Activation code dialog state
  showActivationCodeDialog = signal(false);
  activationCode = signal('');

  constructor(
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly posProfileService: PosProfileService,
  ) {
    this.searchConfig = withPaginatedSearchFields(
      getPosProfileSearchConfig(this.posProfileService),
      {
        channelShopId: {
          loadPage: (params) =>
            this.posProfileService.getPhysicalShopsOptionsPage(params),
        },
      },
    );
  }

  ngOnInit(): void {
    this.loadPosProfile();
  }

  /**
   * Load POS profile terminals based on search parameters
   */
  loadPosProfile(): void {
    this.loading.set(true);

    const searchParams: PosProfileSearchParams = {
      code: this.searchParams().code || undefined,
      channelShopId: this.searchParams().channelShopId,
      status: this.searchParams().status,
      page: this.searchParams().page, // Keep 1-based for API
      size: this.searchParams().size,
    };

    this.posProfileService.getPosProfile(searchParams).subscribe({
      next: (response) => {
        this.posProfile.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading POS profile:', error);
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 1, // Reset to page 1 when searching
      size: this.searchParams().size, // Keep current size
    });
    this.loadPosProfile();
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchParams.set({
      channelShopId: undefined,
      code: '',
      status: undefined,
      page: 1,
      size: 20,
    });
    this.selectedPosProfile.set([]);
    this.loadPosProfile();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant('pos.messages.search_reset'),
    });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1,
    });
    this.loadPosProfile();
  }

  /**
   * Handle selection change
   */
  onSelectionChange(selection: PosProfile[]): void {
    this.selectedPosProfile.set(selection);
  }

  /**
   * Navigate to add new POS profile terminal
   */
  onAdd(): void {
    this.router.navigate(['/pos/profile/add']);
  }

  /**
   * Navigate to edit POS profile terminal
   */
  onEdit(terminal: PosProfile): void {
    this.router.navigate(['/pos/profile/edit', terminal.id]);
  }

  /**
   * Navigate to view POS profile terminal
   */
  onView(terminal: PosProfile): void {
    this.router.navigate(['/pos/profile/view', terminal.id]);
  }

  /**
   * Handle generate activation code
   */
  onGenerate(terminal: PosProfile): void {
    // Hiển thị confirmation dialog
    this.confirmationMessage.set(
      this.translateService.instant('pos.messages.confirm_generate', {
        name: terminal.code,
      }),
    );
    this.confirmationTitle.set(
      this.translateService.instant(
        'pos.dialog.generate_activation_code_title',
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.confirm'),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationAction.set('generate');
    this.confirmationData.set({ terminal });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle status change
   */
  onStatusChange(terminal: PosProfile, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';
    const message = this.translateService.instant(
      `pos.messages.confirm_${action}`,
      { name: terminal.code },
    );
    const title = this.translateService.instant(
      newStatus ? 'pos.dialog.activate_title' : 'pos.dialog.deactivate_title',
    );

    this.confirmationMessage.set(message);
    this.confirmationTitle.set(title);
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          newStatus ? 'pos.actions.activate' : 'pos.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationAction.set('statusChange');
    this.confirmationData.set({ terminal, newStatus });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle delete POS profile terminal
   */
  onDelete(terminal: PosProfile): void {
    if (terminal.status) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'pos.messages.cannot_delete_enabled',
        ),
      });
      return;
    }

    this.confirmationMessage.set(
      this.translateService.instant('pos.messages.confirm_delete', {
        name: terminal.code,
      }),
    );
    this.confirmationTitle.set(
      this.translateService.instant('pos.dialog.delete_title'),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.confirmationAction.set('delete');
    this.confirmationData.set({ terminal });
    this.showConfirmationDialog.set(true);
  }

  /**
   * Handle confirmation dialog actions
   */
  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      switch (this.confirmationAction()) {
        case 'statusChange':
          this.executeStatusChange(
            this.confirmationData().terminal,
            this.confirmationData().newStatus,
          );
          break;
        case 'delete':
          this.executeDelete(this.confirmationData().terminal);
          break;
        case 'generate': // ← THÊM CASE NÀY
          this.executeGenerate(this.confirmationData().terminal);
          break;
      }
    }
  }
  private executeGenerate(terminal: PosProfile): void {
    // Gọi service để generate activation code
    this.posProfileService.generateActivationCode(terminal.id).subscribe({
      next: (response) => {
        if (response?.activationCode) {
          // Hiển thị activation code trong dialog
          this.activationCode.set(response.activationCode);
          this.showActivationCodeDialog.set(true);
          this.loadPosProfile();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'pos.messages.generate_error',
            ),
          });
        }
      },
      error: (error) => {
        console.error('Error generating activation code:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant('pos.messages.generate_error'),
        });
      },
    });
  }

  /**
   * Close activation code dialog
   */
  onCloseActivationCodeDialog(): void {
    this.showActivationCodeDialog.set(false);
    this.activationCode.set('');
  }

  /**
   * Copy activation code to clipboard
   */
  onCopyActivationCode(): void {
    const code = this.activationCode();
    if (code) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'pos.messages.activation_code_copied',
            ),
          });
        })
        .catch(() => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('pos.messages.copy_failed'),
          });
        });
    }
  }

  /**
   * Execute status change
   */
  private executeStatusChange(terminal: PosProfile, newStatus: boolean): void {
    const action = newStatus ? 'enable' : 'disable';

    this.posProfileService
      .updatePosProfileStatus(terminal.id, newStatus)
      .subscribe({
        next: (message) => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              `pos.messages.${action}_success`,
            ),
          });
          this.loadPosProfile();
        },
        error: (error) => {
          console.error('Error updating terminal status:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'pos.messages.status_update_error',
            ),
          });
        },
      });
  }

  /**
   * Execute delete
   */
  private executeDelete(terminal: PosProfile): void {
    this.posProfileService.deletePosProfile(terminal.id).subscribe({
      next: (message) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('pos.messages.delete_success'),
        });
        this.loadPosProfile();
      },
      error: (error) => {
        console.error('Error deleting terminal:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant('pos.messages.delete_error'),
        });
      },
    });
  }

  /**
   * Get status icon class
   */
  getStatusIcon(status: boolean): string {
    if (status) {
      return 'pi pi-check-circle';
    }
    return 'pi pi-times-circle';
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: boolean): string {
    if (status) {
      return this.translateService.instant('common.table.status.active');
    }
    return this.translateService.instant('common.table.status.inactive');
  }

  /**
   * Get status severity
   */
  getStatusSeverity(status: boolean): string {
    if (status) {
      return 'success';
    }
    return 'danger';
  }

  /**
   * Get status style
   */
  getStatusStyle(status: boolean): any {
    if (status) {
      return {
        '--status-color': '#22c55e',
      };
    }
    return {
      '--status-color': '#ef4444',
    };
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
